import React, {useState, useEffect} from 'react';
import {ScrollView, View, Image, Text, Pressable, Modal} from 'react-native';
import {usePowerSyncData} from '../../Providers/PowerSyncDataProvider';
import {ObjectId, UUID} from 'bson';
import {TemplateCategoryTypes} from '../../Constants/TemplateCategoryTypes';
import {Navigation} from '../../Constants/Navigation';
import SearchBox from '../Shared/SearchBox';
import Icon from '../Shared/Icon';
import CustomPressable from '../Shared/CustomPressable';
import {ReportsStyleSheet} from '../../Styles/ReportsStyles';
import {CommonStyleSheet} from '../../Styles/Shared/CommonStyles';
import {ControlsStyleSheet} from '../../Styles/Shared/Controls';
import {QuickAddListSheet} from '../../Styles/QuickAddListSheet';
import {Images} from '../../Constants/Images';
import Colors from '../../Styles/Shared/Colors';
import {useIsFocused} from '@react-navigation/native';
import {Icons} from '../../Constants/Icons';
import DynamicCheckBox from '../DynamicControls/DynamicCheckBox';
import {GeneralDynamicConfig} from '../../Constants/General';
import {PERMISSIONS} from '../../Constants/AppConstants';
import ScreenButtons from '../Shared/ScreenButtons';
import Paginator from '../Shared/Paginator';
import {usePowerSyncUser} from '../../Providers/PowerSyncUserProvider';
import usePowerSyncWriteData from '../../Hooks/usePowerSyncWriteData';
import {ProjectSitesStyleSheet} from '../../Styles/ProjectSiteStyles';

export declare type EquipmentLogProps = {
  navigation: any;
};

type Record = {
  id: string;
  name: string;
  number: string;
  icon: string;
  status: string;
};

const EquipmentLog = (props: EquipmentLogProps): React.ReactElement => {
  const [vehiclesEquipment, setVehiclesEquipment] = useState<Record[]>([]);
  const [heavyEquipment, setHeavyEquipment] = useState<Record[]>([]);
  const [otherEquipment, setOtherEquipment] = useState<Record[]>([]);
  const [search, setSearch] = useState('');
  const isFocused = useIsFocused();

  const DISPLAY_INCREMENT = 5;
  const [showAddModal, setShowAddModal] = useState(false);
  const [showVehicles, setShowVehicles] = useState(false);
  const [showHeavy, setShowHeavy] = useState(false);
  const [showOther, setShowOther] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const [pageDataByCategory, setPageDataByCategory] = useState<{
    [key: string]: Record[];
  }>({});

  const VEHICLETITLE = 'Vehicles';
  const HEAVYTITLE = 'Heavy Equipment';
  const OTHERTITLE = 'Other Equipment';

  const {
    equipmentVehicleProfiles,
    equipmentHeavyProfiles,
    equipmentOtherProfiles,
    powersyncReady,
  } = usePowerSyncData();

  const user = usePowerSyncUser();

  useEffect(() => {
    fetchRecords();
  }, [powersyncReady]);

  useEffect(() => {
    if (isFocused) {
      fetchRecords();
    }
  }, [isFocused, showInactive]);

  const fetchRecords = async () => {
    const vehiclesRecords = equipmentVehicleProfiles
      .filter(
        v =>
          v.partition === user.getUserInfo().orgId.toString() &&
          (v.status == 'Active' || showInactive),
      )
      .map(v => ({
        id: v._id,
        name: v.name,
        number: v.number,
        icon: Icons.VEHICLE,
        status: v.status,
      }));

    vehiclesRecords.sort((a, b) =>
      a.name.toLowerCase() > b.name.toLowerCase()
        ? 1
        : a.name.toLowerCase() < b.name.toLowerCase()
          ? -1
          : 0,
    );

    setVehiclesEquipment(vehiclesRecords);

    const heavyRecords = equipmentHeavyProfiles
      .filter(
        v =>
          v.partition === user.getUserInfo().orgId.toString() &&
          (v.status == 'Active' || showInactive),
      )
      .map(v => ({
        id: v._id,
        name: v.name,
        number: v.number,
        icon: Icons.NAV_EQUIPMENT,
        status: v.status,
      }));

    heavyRecords.sort((a, b) =>
      a.name.toLowerCase() > b.name.toLowerCase()
        ? 1
        : a.name.toLowerCase() < b.name.toLowerCase()
          ? -1
          : 0,
    );

    setHeavyEquipment(heavyRecords);

    const otherRecords = equipmentOtherProfiles
      .filter(
        v =>
          v.partition === user.getUserInfo().orgId.toString() &&
          (v.status == 'Active' || showInactive),
      )
      .map(v => ({
        id: v._id,
        name: v.name,
        number: v.number,
        icon: Icons.TABLET,
        status: v.status,
      }));

    otherRecords.sort((a, b) =>
      a.name.toLowerCase() > b.name.toLowerCase()
        ? 1
        : a.name.toLowerCase() < b.name.toLowerCase()
          ? -1
          : 0,
    );

    setOtherEquipment(otherRecords);
  };

  async function recordPressed(type: string, recordId?: string): Promise<void> {
    setShowAddModal(false);

    let navType = '';
    if (type === HEAVYTITLE) {
      navType = Navigation.HEAVYPROFILE;
    } else if (type === VEHICLETITLE) {
      navType = Navigation.VEHICLEPROFILE;
    } else if (type === OTHERTITLE) {
      navType = Navigation.OTHERPROFILE;
    }

    props.navigation.push(navType, {
      equipmentId: recordId,
    });
  }

  const goBack = () => {
    props.navigation.pop();
  };

  const renderRecord = (
    record: Record,
    type: string,
    index: number,
  ): React.ReactElement => {
    let status = '';
    let statusObj = GeneralDynamicConfig.SEGMENT_STATUS.segments.find(
      x => x.value == record.status,
    );
    if (statusObj && statusObj.value != 'Active')
      status = ' - ' + statusObj.name;

    return (
      <View
        key={index}
        style={{
          flexDirection: 'row',
          borderBottomWidth: 0.4,
          borderBottomColor: Colors.darkGreen,
        }}>
        <Pressable
          style={({pressed}) => [
            ReportsStyleSheet.listItemContainer,
            {flex: 1},
            pressed && {
              backgroundColor: Colors.darkGreenTransparent,
            },
          ]}
          onPress={() => recordPressed(type, record.id)}>
          <View style={{marginHorizontal: 12}}>
            <img
              src={`data:image/svg+xml;utf8,${encodeURIComponent(
                record.icon.replaceAll('currentColor', Colors.darkestGreen),
              )}`}
            />
          </View>
          <Text style={ReportsStyleSheet.listItemName} numberOfLines={2}>
            {record.name} - {record.number}
            {status}
          </Text>
          <View style={{marginHorizontal: 12}}>
            <img
              src={`data:image/svg+xml;utf8,${encodeURIComponent(
                Icons.LIST_ITEM_NAV.replaceAll(
                  'currentColor',
                  Colors.darkestGreen,
                ),
              )}`}
            />
          </View>
        </Pressable>
      </View>
    );
  };

  const renderCategory = (
    records: Record[],
    title: string,
  ): React.ReactElement => {
    const filteredRecords: Record[] = search
      ? records.filter(r =>
          (r.name + ' - ' + r.number)
            .toLowerCase()
            .includes(search.toLowerCase()),
        )
      : records;

    if (filteredRecords.length === 0) return <React.Fragment></React.Fragment>;

    const paginator = (
      <Paginator
        data={filteredRecords}
        pageSize={DISPLAY_INCREMENT}
        pageSizeOptions={[5, 10, 25, 50, 100]}
        onPaginatedDataChange={paged =>
          setPageDataByCategory(prev => ({...prev, [title]: paged}))
        }
        searchDeps={[search, showInactive, records]}
      />
    );

    const pageData = pageDataByCategory[title] || [];

    return (
      <>
        <Text style={[CommonStyleSheet.controlLabel, {marginTop: 24}]}>
          {title}
        </Text>
        {pageData.map((record, i) => {
          return renderRecord(record, title, i);
        })}
        <View style={{marginTop: 24}}>{paginator}</View>
      </>
    );
  };

  const renderRecords = (): React.ReactElement => {
    let hasTypeFilter = showVehicles || showHeavy || showOther;

    return (
      <View style={{paddingHorizontal: 20, paddingBottom: 20}}>
        {(!hasTypeFilter || showVehicles) &&
          renderCategory(vehiclesEquipment, VEHICLETITLE)}
        {(!hasTypeFilter || showHeavy) &&
          renderCategory(heavyEquipment, HEAVYTITLE)}
        {(!hasTypeFilter || showOther) &&
          renderCategory(otherEquipment, OTHERTITLE)}
      </View>
    );
  };

  const renderFilters = (): React.ReactElement => {
    return (
      <>
        <SearchBox onChangeSearch={newSearch => setSearch(newSearch)} />
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 20,
            marginHorizontal: 20,
            marginTop: 6,
            flexWrap: "wrap"
          }}>
          <Text style={[ControlsStyleSheet.checkBoxText]}>Filter:</Text>
          <DynamicCheckBox
            label='Vehicles'
            value={showVehicles ? 'true' : 'false'}
            onChange={(controlId, controlTypeId, value) =>
              setShowVehicles(value === 'true')
            }
          />
          <DynamicCheckBox
            label='Heavy Equipment'
            value={showHeavy ? 'true' : 'false'}
            onChange={(controlId, controlTypeId, value) =>
              setShowHeavy(value === 'true')
            }
          />
          <DynamicCheckBox
            label='Other Equipment'
            value={showOther ? 'true' : 'false'}
            onChange={(controlId, controlTypeId, value) =>
              setShowOther(value === 'true')
            }
          />
          <DynamicCheckBox
            label='Show Inactive'
            value={showInactive ? 'true' : 'false'}
            onChange={(controlId, controlTypeId, value) =>
              setShowInactive(value === 'true')
            }
          />
        </View>
      </>
    );
  };

  const renderModal = (): React.ReactElement => {
    return (
      <Modal
        visible={showAddModal}
        transparent={true}
        statusBarTranslucent={true}
        animationType='fade'>
        <View style={ControlsStyleSheet.modalBackground}>
          <View style={QuickAddListSheet.quickAddListContainer}>
            <ScrollView style={{padding: 20}}>
              <Text style={QuickAddListSheet.quickAddListHeading}>
                Equipment Type
              </Text>
              <React.Fragment>
                <Pressable
                  onPress={() => {
                    recordPressed(VEHICLETITLE);
                  }}
                  style={({pressed}) => [
                    pressed && {
                      opacity: 0.8,
                    },
                    QuickAddListSheet.quickAddListItem,
                  ]}>
                  <img
                    src={`data:image/svg+xml;utf8,${encodeURIComponent(
                      Icons.VEHICLE.replaceAll('currentColor', Colors.white),
                    )}`}
                  />
                  <Text style={QuickAddListSheet.quickAddListText}>
                    {VEHICLETITLE}
                  </Text>
                </Pressable>
                <Pressable
                  onPress={() => {
                    recordPressed(HEAVYTITLE);
                  }}
                  style={({pressed}) => [
                    pressed && {
                      opacity: 0.8,
                    },
                    QuickAddListSheet.quickAddListItem,
                  ]}>
                  <img
                    src={`data:image/svg+xml;utf8,${encodeURIComponent(
                      Icons.NAV_EQUIPMENT.replaceAll(
                        'currentColor',
                        Colors.white,
                      ),
                    )}`}
                  />
                  <Text style={QuickAddListSheet.quickAddListText}>
                    {HEAVYTITLE}
                  </Text>
                </Pressable>
                <Pressable
                  onPress={() => {
                    recordPressed(OTHERTITLE);
                  }}
                  style={({pressed}) => [
                    pressed && {
                      opacity: 0.8,
                    },
                    QuickAddListSheet.quickAddListItem,
                  ]}>
                  <img
                    src={`data:image/svg+xml;utf8,${encodeURIComponent(
                      Icons.TABLET.replaceAll('currentColor', Colors.white),
                    )}`}
                  />
                  <Text style={QuickAddListSheet.quickAddListText}>
                    {OTHERTITLE}
                  </Text>
                </Pressable>
              </React.Fragment>
            </ScrollView>
          </View>
          <Pressable
            style={({pressed}) => [
              QuickAddListSheet.quickAddButton,
              pressed && {
                opacity: 0.8,
              },
            ]}
            onPress={() => setShowAddModal(false)}>
            <Image
              resizeMode='contain'
              style={{width: '100%', height: '100%'}}
              source={Images.CLOSE}
            />
          </Pressable>
        </View>
      </Modal>
    );
  };

  return (
    <ScrollView>
      <View style={{height: 260, backgroundColor: Colors.darkestGreen}}>
        <Pressable
          hitSlop={40}
          style={({pressed}) => [
            {
              width: 42,
              height: 42,
              borderRadius: 42,
              padding: 5,
            },
            pressed && {
              backgroundColor: Colors.teal,
            },
          ]}
          onPress={() => goBack()}>
          <img
            src={`data:image/svg+xml;utf8,${encodeURIComponent(
              Icons.CHEVRON_LEFT.replaceAll('currentColor', Colors.white),
            )}`}
          />
        </Pressable>
        <Text style={ReportsStyleSheet.headerTitle}>Equipment Log</Text>
      </View>
      <View style={{flexDirection: 'row'}}>
        <View style={{flex: 2, padding: 28}}>
          <View style={{marginTop: -58, marginLeft: 'auto', marginRight: 28}}>
            <CustomPressable
              style={({pressed}) => [
                {
                  backgroundColor: Colors.green,
                  width: 58,
                  height: 58,
                  borderRadius: 58,
                },
                pressed && {
                  backgroundColor: Colors.teal,
                },
              ]}
              onPress={() => setShowAddModal(true)}
              tooltip='Add New'>
              <View style={{marginTop: -1, marginLeft: -1}}>
                <Icon icon={'add'} color={Colors.white} size={60} />
              </View>
            </CustomPressable>
          </View>
          {renderFilters()}
          {renderRecords()}
          {vehiclesEquipment.filter(
            r =>
              r.name?.toLowerCase().includes(search.toLowerCase()) ||
              r.number?.toLowerCase().includes(search.toLowerCase()),
          ).length == 0 &&
          heavyEquipment.filter(
            r =>
              r.name?.toLowerCase().includes(search.toLowerCase()) ||
              r.number?.toLowerCase().includes(search.toLowerCase()),
          ).length == 0 &&
          otherEquipment.filter(
            r =>
              r.name?.toLowerCase().includes(search.toLowerCase()) ||
              r.number?.toLowerCase().includes(search.toLowerCase()),
          ).length == 0 ? (
            <View style={ReportsStyleSheet.list}>
              <Text
                style={[
                  ProjectSitesStyleSheet.siteItemText,
                  {textAlign: 'center', color: Colors.darkestGreen},
                ]}>
                No Results Found
              </Text>
            </View>
          ) : null}
        </View>
        <View style={{flex: 1}}>
          <ScreenButtons
            navigation={props.navigation}
            category={TemplateCategoryTypes.EQUIPMENT}
            permissions={PERMISSIONS.REPORT_EQUIPMENT_CREATE}
            showExtraButtons={false}
          />
          <View style={CommonStyleSheet.screenMessageContainer}>
            <Text style={CommonStyleSheet.screenMessageText}>
              Don't forget to add new vehicles, and update vehicles that may not
              be in service.
            </Text>
          </View>
        </View>
      </View>
      {renderModal()}
    </ScrollView>
  );
};

export default EquipmentLog;
